import React from "react"
import {
  Box,
  useTheme,
  Slide,
  SlideProps,
  Typography,
  Avatar,
} from "@mui/material"
import { BaseModal, IBaseModalProps } from "components/modal"
import { useTranslation } from "providers/i18n"
import { getIcon } from "utils/icon"
import { IFeatureDetailResult } from "hooks/feature"
import { IOccupantDetail } from "utils/feature"

import { Carousel, CarouselItem } from "components/Carousel"
import { StoreIcon } from "components/icons"
import { ReservationQRCodeCard } from "./QrCodeCard"
import { ReservationContactCard } from "./ReservationContactCard"
import { useOccupantReservations } from "./hooks"

const ModalTransition = React.forwardRef<HTMLElement, SlideProps>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
  }
)

export interface IReservationModalProps
  extends Omit<IBaseModalProps, "children"> {
  data: Partial<IFeatureDetailResult>
}

export const ReservationModal: React.FC<IReservationModalProps> = ({
  data = {},
  sx = {},
  onClose,
  open = true,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const {
    root,
    reservationContainer,
    shopContainer,
    shopInfoContainer,
    avatar,
    label,
    titleContainer,
    titleText,
    carousel,
    carouselItem,
    reservationCard,
  } = theme.components?.[`ReservationModal`].styleOverrides || {}
  const { carouselOptions = {} } =
    theme.components?.[`ReservationModal`].defaultProps || {}

  const { name, logo } = data as IOccupantDetail

  const { reservationMethods, contactMethod } = useOccupantReservations(data)
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      sx={sx}
      {...props}
      TransitionComponent={ModalTransition}
    >
      <Box sx={root}>
        <Box sx={shopContainer}>
          <Box sx={shopInfoContainer}>
            <Avatar sx={avatar} src={logo}>
              {getIcon(data.categoryIconProp, null) || <StoreIcon />}
            </Avatar>
            <Typography sx={label} gutterBottom variant="h5">
              {t(name)}
            </Typography>
          </Box>
          <Box sx={titleContainer}>
            <Typography sx={titleText} gutterBottom variant="h5">
              {t("Reservation choices")}
            </Typography>
          </Box>
        </Box>
        <Box sx={reservationContainer}>
          <Carousel
            options={{
              pagination: false,
              perPage: 4,
              perMove: 4,
              focus: 0,
              gap: "12px",
              autoWidth: true,
              arrows: true,
              width: 904,
              ...carouselOptions,
            }}
            sx={carousel}
          >
            {reservationMethods.map((data, index) => {
              return (
                <CarouselItem key={`slide-${index}`} sx={carouselItem}>
                  <ReservationQRCodeCard data={data} sx={reservationCard} />
                </CarouselItem>
              )
            })}
            {contactMethod && (
              <CarouselItem>
                <ReservationContactCard
                  data={contactMethod}
                  sx={reservationCard}
                />
              </CarouselItem>
            )}
          </Carousel>
        </Box>
      </Box>
    </BaseModal>
  )
}
